import React, { useEffect, useState } from 'react';

import { Container, FormControl, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import APIError from 'components/AlertScreens/APIError';
import ListSkeletonLoader from 'components/AlertScreens/ListSkeletonLoader';
import useAsync from 'components/hooks/useAsync';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { TemplateQuestion } from 'models/templates/review';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Divider1, StyledBox, StyledGrid, StyledLinearProgress } from 'styles/common';
import { checkGoalModuleEnabled } from 'utils/helpers/moduleAccess';

import { QUESTION_ORDER_ACTIONS, REVIEW_QUESTION_TYPES, REVIEW_QUESTION_TYPE_MAP_2 } from 'constants/ProjectConstants';
import { swapElementsInArray } from 'constants/helper';

import { showErrorSnackbar } from 'services/snackbar';
import { getReviewTemplateQuestions, updateTemplateStructure } from 'services/templates/review';

import { Typography1, Typography10, Typography11 } from 'uiToolkit/Typography';

import NoQuestionsScreen from '../../../../../components/Templates/NoQuestionsScreen';
import { useQuestionListSidePanel } from '../../../common/TemplateCommons/QuestionListSidepanel/context/QuestionListSidepanelContext';
import { useReviewTemplate } from '../common/ReviewTemplateContext';
import { ContentContainer, WhiteBGStyledGrid } from '../common/styles';
import QuestionRowType1 from './common/QuestionRowType1';

const ADD_RESTRICTION_MESSAGE = 'Only one question per individual goal is allowed in a template';

const QuestionListHead = ({ isEnableGoalLevelQuestion }) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  return (
    <StyledBox elevation={0} paddingRight="10px">
      <Divider1 />
      <WhiteBGStyledGrid container justify="space-between" padding="0.5rem 1rem">
        <StyledGrid item xs={isEnableGoalLevelQuestion ? 2 : 4}>
          <Typography10 color="textSecondary" variant="body2">
            {isEnableGoalLevelQuestion ? `${moduleName} Name` : 'Question Text'}
          </Typography10>
        </StyledGrid>

        {isEnableGoalLevelQuestion && (
          <StyledGrid item xs={2}>
            <Typography10 color="textSecondary" variant="body2">
              {' '}
              Progress
            </Typography10>
          </StyledGrid>
        )}

        <StyledGrid item xs={4}>
          <Typography10 color="textSecondary" variant="body2">
            Score
          </Typography10>
        </StyledGrid>
        <StyledGrid item xs={4}>
          <Typography10 color="textSecondary" variant="body2">
            Your Feedback
          </Typography10>
        </StyledGrid>
      </WhiteBGStyledGrid>
      <Divider1 />
    </StyledBox>
  );
};

export const DummyGoalRow = () => (
  <StyledBox paddingRight="10px">
    <Divider1 />
    <WhiteBGStyledGrid container padding="2rem 1rem">
      <StyledGrid item xs={2}>
        Reviewee Sample Goal
      </StyledGrid>
      <StyledGrid item xs={2}>
        <FullWidthBox flexcontainer flexdirection="column" alignitems="center" justifycontent="center">
          <Typography10 variant="caption" color="textSecondary">
            9M
          </Typography10>
          <FullWidthBox flexcontainer justifycontent="center" alignitems="center">
            <StyledLinearProgress variant="determinate" value={90} color="primary" barColor="green" />
            <Typography10 variant="caption" color="textSecondary">
              10M
            </Typography10>
          </FullWidthBox>
        </FullWidthBox>
      </StyledGrid>
    </WhiteBGStyledGrid>
    <Divider1 />
  </StyledBox>
);

const GoalReview = () => {
  const [selectedId, setSelectedId] = useState({});
  const { showSidepanel, hideSidepanel } = useQuestionListSidePanel();
  const dispatch = useDispatch();
  const {
    state: { META, PERFORMANCE },
    setMeta,
    setQuestions,
    addQuestion,
    removeQuestion,
  } = useReviewTemplate();

  const { templateQuestions } = PERFORMANCE;

  const { data, run, isLoading, isError } = useAsync();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const getData = () => {
    run(
      getReviewTemplateQuestions({
        templateId: META.id,
        section: REVIEW_QUESTION_TYPES.PERFORMANCE,
      })
    );
  };

  useEffect(() => {
    if (META.id) {
      getData();
    }
  }, []);

  useEffect(() => {
    data && setQuestions({ section: REVIEW_QUESTION_TYPES.PERFORMANCE, questions: data.goalQuestions });
  }, [data]);

  const onQuestionAddition = ({ question, insertionPosition }) => {
    const templateQuestion = TemplateQuestion.fromJSON({
      templateId: META.id,
      question,
    });
    addQuestion({
      question: templateQuestion,
      section: REVIEW_QUESTION_TYPES.PERFORMANCE,
      insertionPosition,
    });
  };

  const handleAddQuestion = ({ insertionPosition }) => {
    showSidepanel({
      open: true,
      onSuccess: ({ question, insertionPosition }) => {
        onQuestionAddition({ question, insertionPosition });
        if (META.templateSetting.isEnableGoalLevelQuestion) {
          hideSidepanel();
        }
      },
      // Questions can be of ["GOAL", "COMPETENCY", "QNA"] and we need to show only of GOAL aka PERFORMANCE type for sidepanel opened from GoalReview.
      type: REVIEW_QUESTION_TYPE_MAP_2.PERFORMANCE,
      insertionPosition,
    });
  };

  const handleRemoveQuestion = ({ index }) => {
    removeQuestion({
      section: REVIEW_QUESTION_TYPES.PERFORMANCE,
      index,
    });
  };

  const handleGoalQuestionTypeChange = (e, value) => {
    const newMeta = {
      ...META,
      templateSetting: { ...META.templateSetting, isEnableGoalLevelQuestion: value === 'true' },
    };
    // when switching from 'overall' to 'individual', keep only one(first) question as individual supports only one
    if (value === 'true') {
      setQuestions({
        section: REVIEW_QUESTION_TYPES.PERFORMANCE,
        questions: templateQuestions.length >= 1 ? [templateQuestions[0]] : [],
      });
    }
    setMeta({ meta: newMeta });
    updateTemplateStructure({ templateId: META.id, body: newMeta }).catch((err) =>
      dispatch(showErrorSnackbar(err.message || 'Something went wrong!'))
    );
  };
  // using useEffect to manually set the goalQuestionType because by default it is true from BE,
  // but if the GOAL module is OFF, then it should be false
  // ask BE to provide default value as false if the module is OFF, and remove the useEffect.
  useEffect(() => {
    !checkGoalModuleEnabled() && handleGoalQuestionTypeChange(null, 'false');
  }, []);

  const handleQuestionDataChange = ({ param, value, index }) => {
    const newTemplateQuestion = {
      ...templateQuestions[index],
      [param]: value,
    };
    const updatedQuestions = [...templateQuestions];
    updatedQuestions[index] = newTemplateQuestion;
    setQuestions({ section: REVIEW_QUESTION_TYPES.PERFORMANCE, questions: updatedQuestions });
  };

  const handleQuestionOrderChange = ({ index, action }) => {
    let newQuestionList = [];

    if (action === QUESTION_ORDER_ACTIONS.MOVE_UP) {
      newQuestionList = swapElementsInArray(templateQuestions, index - 1, index);
    } else {
      newQuestionList = swapElementsInArray(templateQuestions, index + 1, index);
    }
    setQuestions({ section: REVIEW_QUESTION_TYPES.PERFORMANCE, questions: newQuestionList });
  };

  const allowAddingMoreThanOneQuestion = !META.templateSetting.isEnableGoalLevelQuestion;

  const handleQuestionSelect = ({ index }) => {
    setSelectedId({ index });
  };

  return (
    <Container maxWidth="md">
      <Typography11 bottomGutter={2}>{moduleName} Form Structure</Typography11>
      <ContentContainer transparent>
        {isError ? (
          <APIError reloadHandler={() => getData()} />
        ) : isLoading ? (
          <ListSkeletonLoader />
        ) : (
          <>
            {checkGoalModuleEnabled() && (
              <StyledBox paddingRight="10px">
                <WhiteBGStyledGrid padding="1rem" container justify="space-between">
                  <StyledGrid item xs={8}>
                    <Typography1 fontsize="16px" variant="body1">
                      Please select how questions will be linked to {moduleName}
                    </Typography1>
                    <Typography1 fontsize="14px" variant="caption" color="textSecondary">
                      You can add common or individual questions for all {moduleName}
                    </Typography1>
                  </StyledGrid>
                  <StyledGrid item xs={4}>
                    <StyledFormControl component="fieldset">
                      <RadioGroup
                        aria-label="Question Type"
                        name="Question Type"
                        value={META.templateSetting.isEnableGoalLevelQuestion}
                        onChange={handleGoalQuestionTypeChange}
                      >
                        <FormControlLabel
                          value
                          control={<Radio color="primary" />}
                          label={`Individual ${moduleName}`}
                        />
                        <FormControlLabel
                          value={false}
                          control={<Radio color="primary" />}
                          label={`Overall ${moduleName}`}
                        />
                      </RadioGroup>
                    </StyledFormControl>
                  </StyledGrid>
                </WhiteBGStyledGrid>
              </StyledBox>
            )}
            <QuestionListHead isEnableGoalLevelQuestion={META.templateSetting.isEnableGoalLevelQuestion} />
            {META.templateSetting.isEnableGoalLevelQuestion && templateQuestions.length ? <DummyGoalRow /> : null}

            {templateQuestions?.length ? (
              templateQuestions.map((item, idx) => (
                <QuestionRowType1
                  key={item.id || idx}
                  addQuestionHandler={handleAddQuestion}
                  questionObject={item}
                  handleRemoveQuestion={handleRemoveQuestion}
                  handleQuestionDataChange={handleQuestionDataChange}
                  questionOrderChangeHandler={handleQuestionOrderChange}
                  isFirstItem={idx === 0}
                  isLastItem={idx + 1 === templateQuestions.length}
                  index={idx}
                  type={REVIEW_QUESTION_TYPES.PERFORMANCE}
                  isAddQuestionEnabled={allowAddingMoreThanOneQuestion}
                  addQuestionTooltip={allowAddingMoreThanOneQuestion ? '' : ADD_RESTRICTION_MESSAGE}
                  handleQuestionSelect={handleQuestionSelect}
                  selectedId={selectedId}
                />
              ))
            ) : (
              <StyledBox paddingRight="10px">
                <NoQuestionsScreen addQuestionHandler={handleAddQuestion} />
              </StyledBox>
            )}
          </>
        )}
      </ContentContainer>
    </Container>
  );
};

export default GoalReview;

const StyledFormControl = styled(FormControl)`
  width: 100%;
  .MuiFormGroup-root {
    align-items: center;
    flex-direction: row;
    justify-content: space-evenly;
  }
`;

const FullWidthBox = styled(StyledBox)`
  width: 100%;
`;
